import React, { useRef, useCallback, useMemo, useEffect } from "react";
import { FlatList, Platform, RefreshControl, View, StyleSheet, Text } from "react-native";

import CustomLinearGradient, { GRADIENT_TYPE } from "../../../components/CustomLinearGradient";
import TemplateHeader from "../../../components/grid/TemplateHeader";
import TemplateRow from "../../../components/grid/TemplateRow";

import { useThemeSettings } from "../../../ThemeProvider";
import { useCollectionStore } from "../../../store/collectionStore";

interface NewsFeedListProps {
  rows: any[];
  scrollViewRef: React.RefObject<FlatList>;
  handleScroll: (event: any) => void;
  handleRefresh: () => void;
  isRefreshing: boolean;
  isFetchingPreviousPage: boolean;
  fromRadio: boolean;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onScrollBeginDrag: () => void;
  onMomentumScrollEnd: () => void;
  onScrollToIndexFailed: (info: any) => void;
}

const NewsFeedList: React.FC<NewsFeedListProps> = ({
  rows,
  scrollViewRef,
  handleScroll,
  handleRefresh,
  isRefreshing,
  isFetchingPreviousPage,
  fromRadio,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  onScrollBeginDrag,
  onMomentumScrollEnd,
  onScrollToIndexFailed
}) => {
  const { theme } = useThemeSettings();
  const { setRecheckCollection } = useCollectionStore();

  // Track if user has scrolled past 10 rows
  const isNearTopRef = useRef(true);
  const onViewableItemsChanged = useRef(({ viewableItems }) => {
    if (!viewableItems?.length) return;
  
    const firstVisibleIndex = viewableItems[0].index ?? 0;
  
    // Trigger when crossing 10th row from top
    if (firstVisibleIndex > 10 && isNearTopRef.current) {
      setRecheckCollection(true);
      isNearTopRef.current = false; 
    }
  
    // Reset lock if user scrolls near top
    if (firstVisibleIndex <= 2) {
      isNearTopRef.current = true;
    }
  }).current;

  // Memoize row rendering
  const renderItem = useCallback(
    ({ item }) => <TemplateRow {...item} fromRadio={fromRadio} />,
    [fromRadio]
  );

  // Memoize footer
  const ListFooterComponent = useMemo(() => (
    isFetchingNextPage ? <Text style={styles.loading}>Loading…</Text> : null
  ), [isFetchingNextPage]);

  // Memoize RefreshControl
  const refreshControl = useMemo(() => (
    <RefreshControl
      refreshing={isRefreshing || isFetchingPreviousPage}
      onRefresh={handleRefresh}
      tintColor={theme?.colors?.primary}
      colors={[theme?.colors?.primary]}
    />
  ), [isRefreshing, isFetchingPreviousPage, handleRefresh, theme]);

  return (
    <View style={styles.container}>
      {/* Rendered outside FlatList so touch events always work after scrolling */}
      <View style={styles.headerWrapper}>
        <CustomLinearGradient
          gradientType={GRADIENT_TYPE.APP_CARD}
          style={styles.headerGradient}
        >
          <TemplateHeader />
        </CustomLinearGradient>
      </View>

      <FlatList
        ref={scrollViewRef}
        data={rows}
        keyExtractor={(item) => item.id}
        renderItem={renderItem}

        /* performance */
        windowSize={11}
        maxToRenderPerBatch={6}
        initialNumToRender={6}
        removeClippedSubviews

        /* scroll */
        scrollEventThrottle={16}
        showsVerticalScrollIndicator={false}

        onScroll={handleScroll}
        onScrollBeginDrag={onScrollBeginDrag}
        onMomentumScrollEnd={onMomentumScrollEnd}

        onScrollToIndexFailed={onScrollToIndexFailed}

        /* pagination */
        onEndReached={() => {
          if (hasNextPage && !isFetchingNextPage) fetchNextPage();
        }}
        onEndReachedThreshold={0.4}

        refreshControl={refreshControl}
        ListFooterComponent={ListFooterComponent}
        onViewableItemsChanged={onViewableItemsChanged}
        viewabilityConfig={{ itemVisiblePercentThreshold: 60 }}
      />
    </View>
  );
};

// Wrap with React.memo and custom comparison to prevent unnecessary rerenders

export default React.memo(
  NewsFeedList,
  (prev, next) =>
    prev.rows === next.rows &&
    prev.isRefreshing === next.isRefreshing &&
    prev.isFetchingPreviousPage === next.isFetchingPreviousPage &&
    prev.fromRadio === next.fromRadio &&
    prev.hasNextPage === next.hasNextPage &&
    prev.isFetchingNextPage === next.isFetchingNextPage
);

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  headerWrapper: {
    width: "100%",
    zIndex: 10
  },
  headerGradient: {
    borderWidth: 0,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0
  },
  loading: {
    textAlign: "center",
    padding: 16
  }
});